import { useEffect, useState } from 'react'
import { X } from 'lucide-react'
import { IoMdArrowBack } from 'react-icons/io'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import toast from 'react-hot-toast'
import { fetchCategories, fetchUserCategories, selectCategories } from '../../redux/ApiSlice/CategorySlice'

const ManageInterests = () => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const { allCategories, loading, submitting } = useSelector((state) => state.Category)

    const [pendingSlugs, setPendingSlugs] = useState(null)

    useEffect(() => {
        const load = async () => {
            await dispatch(fetchCategories())
            const result = await dispatch(fetchUserCategories())
            const cats = result.payload ?? []
            setPendingSlugs(new Set(cats.map((c) => c.slug)))
        }
        load()
    }, [dispatch])

    const toggle = (slug) => {
        setPendingSlugs((prev) => {
            const next = new Set(prev)
            if (next.has(slug)) {
                next.delete(slug)
            } else {
                if (next.size >= 5) {
                    toast.error('You can select a maximum of 5 interests.')
                    return prev
                }
                next.add(slug)
            }
            return next
        })
    }

    const handleSave = async () => {
        if (!pendingSlugs || pendingSlugs.size === 0) {
            toast.error('Select at least 1 interest.')
            return
        }
        try {
            await dispatch(selectCategories([...pendingSlugs])).unwrap()
            toast.success('Interests updated!')
        } catch (err) {
            toast.error(err?.message || 'Failed to update interests.')
        }
    }

    const isDataReady = !loading && pendingSlugs !== null

    return (
        <div className="h-full bg-white dark:bg-[#1E1D21] flex flex-col">
            {/* Header */}
            <div className="sticky top-0 z-10">
                <div className="relative flex items-center justify-center h-12 bg-[#BDE6FF] rounded-b-xl px-2">
                    <IoMdArrowBack
                        onClick={() => navigate('/account/settings')}
                        className="absolute left-2 top-1/2 -translate-y-1/2 text-[22px] text-black cursor-pointer"
                    />
                    <p className="font-bold text-[16px] text-black">My Interests</p>
                </div>
            </div>

            <div className="flex-1 overflow-y-auto px-3 pt-3 pb-24">
                {!isDataReady ? (
                    <div className="flex justify-center items-center py-16">
                        <svg className="animate-spin h-6 w-6 text-[#BDE6FF]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                    </div>
                ) : (
                    <>
                        {/* Selected chips */}
                        <div className="mb-4">
                            <p className="text-[13px] font-bold text-black dark:text-white mb-2">
                                Selected ({pendingSlugs.size} / 5)
                            </p>
                            {pendingSlugs.size === 0 ? (
                                <p className="text-[12px] text-gray-400">None selected yet.</p>
                            ) : (
                                <div className="flex flex-wrap gap-2">
                                    {allCategories.flatMap((g) => g.items)
                                        .filter((item) => pendingSlugs.has(item.slug))
                                        .map((item) => (
                                            <span
                                                key={item.slug}
                                                className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#BDE6FF] border border-[#a2d9fb] text-[12px] font-semibold text-black"
                                            >
                                                <span>{item.emoji}</span>
                                                <span>{item.name}</span>
                                                <button
                                                    onClick={() => toggle(item.slug)}
                                                    className="ml-1 flex items-center justify-center"
                                                >
                                                    <X size={12} strokeWidth={2.5} />
                                                </button>
                                            </span>
                                        ))}
                                </div>
                            )}
                        </div>

                        <div className="border-t border-gray-200 dark:border-gray-700 mb-4" />

                        {/* All categories */}
                        <p className="text-[13px] font-bold text-black dark:text-white mb-3">All Categories</p>
                        <div className="flex flex-col gap-5">
                            {allCategories.map((cat) => (
                                <div key={cat.label}>
                                    <p className="text-[13px] font-semibold text-black dark:text-white mb-2">{cat.label}</p>
                                    <div className="flex flex-wrap gap-2">
                                        {cat.items.map((item) => {
                                            const active = pendingSlugs.has(item.slug)
                                            return (
                                                <button
                                                    key={item.slug}
                                                    onClick={() => toggle(item.slug)}
                                                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-[12px] font-semibold transition-colors
                                                        ${active
                                                            ? 'bg-[#BDE6FF] border-[#a2d9fb] text-black'
                                                            : 'bg-white dark:bg-[#333333] border-gray-200 dark:border-gray-700 text-gray-800 dark:text-gray-400 hover:border-gray-400 dark:hover:border-gray-600'
                                                        }`}
                                                >
                                                    <span>{item.emoji}</span>
                                                    <span>{item.name}</span>
                                                </button>
                                            )
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>

            {/* Save button */}
            <div className="sticky bottom-0 bg-white dark:bg-[#1E1D21] px-3 py-3 border-t border-gray-200 dark:border-gray-700">
                <button
                    onClick={handleSave}
                    disabled={submitting || !isDataReady}
                    className={`w-full py-3 rounded-xl text-[14px] font-semibold flex items-center justify-center gap-2 transition-colors
                        ${submitting || !isDataReady
                            ? 'bg-gray-200 dark:bg-[#333333] text-gray-400 cursor-not-allowed'
                            : 'bg-[#BDE6FF] text-black hover:bg-[#a2d9fb]'
                        }`}
                >
                    {submitting ? (
                        <svg className="animate-spin h-4 w-4 text-black" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                    ) : 'Save Changes'}
                </button>
            </div>
        </div>
    )
}

export default ManageInterests
